import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import {
  IconBillCheck,
  IconChart,
  IconEdit,
  IconIdCard,
  IconPrinter,
  IconRefreshCw,
  IconStethoscope,
  IconStore,
  IconWallet,
} from '../../components/layout/icons';
import { BillActionGrid, BillActionHoverTile, BillActionTile } from '../../components/ui/BillActionTile';
import { EmptyState, ErrorState } from '../../components/ui/Feedback';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { openAuthenticatedRoute } from '../../lib/auth';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { IpdRequisitionPanel } from '../ipd-daycare/IpdRequisitionPanel';
import { InvestigationBillAllBillList } from './InvestigationBillAllBillList';
import { InvestigationBillAllReceiptList } from './InvestigationBillAllReceiptList';
import { InvestigationBillAllRefundList } from './InvestigationBillAllRefundList';
import { InvestigationBillEditBillForm } from './InvestigationBillEditBillForm';
import { useInvestigationBill } from './investigation-bill.queries';
import { currency, formatInvestigationDateTime } from './investigation-bill.utils';
import type { InvestigationBill } from './types';

type BillTab = 'bill-details' | 'print-bill' | 'all-bill' | 'prescription' | 'edit-bill' | 'requisition' | 'all-receipt' | 'all-refund';
type RequisitionKind = 'pathology' | 'non-pathology' | 'radiology';

const blankTabs: Array<{ key: Exclude<BillTab, 'bill-details'>; label: string; icon: ComponentType<SVGProps<SVGSVGElement>>; soon: boolean; message: string }> = [
  { key: 'print-bill', label: 'Print Bill', icon: IconPrinter, soon: false, message: 'This section is not available here yet. Use the Print button at the top of the page to print this bill.' },
  { key: 'all-bill', label: 'All Bill', icon: IconWallet, soon: false, message: 'A consolidated bill list is not available for Investigation bills yet.' },
  { key: 'prescription', label: 'Prescription', icon: IconStethoscope, soon: true, message: 'Prescription records are not available for Investigation bills yet.' },
  { key: 'edit-bill', label: 'Edit Bill', icon: IconEdit, soon: false, message: 'Editing an Investigation bill is not available yet.' },
  { key: 'all-receipt', label: 'All Receipt', icon: IconChart, soon: false, message: 'A receipts list is not available for Investigation bills yet.' },
  { key: 'all-refund', label: 'All Refund', icon: IconRefreshCw, soon: false, message: 'A refunds list is not available for Investigation bills yet.' },
];

const requisitionKindForLabel = (label: string): RequisitionKind | null => {
  const value = label.toLowerCase();
  if (value.includes('non') && value.includes('pathology')) return 'non-pathology';
  if (value.includes('radiology')) return 'radiology';
  if (value.includes('pathology')) return 'pathology';
  return null;
};

const requisitionKindForSection = (section: InvestigationBill['charges'][number]['requisitionSection']): RequisitionKind | null =>
  requisitionKindForLabel(`${section?.name ?? ''} ${section?.legacyId ?? ''}`);

const requisitionKindForChargeName = (chargeName: string): RequisitionKind => {
  const value = chargeName.toLowerCase();
  if (/\b(x[\s-]?ray|ct|mri|usg|ultra\s?sound|sonography|doppler|kub|chest pa|pns|scan|radiology)\b/.test(value)) return 'radiology';
  if (/\b(cbc|blood|urine|stool|culture|pathology|hiv|hbsag|hba1c|lft|kft|tsh|t3|t4|bilirubin|creatinine|urea|sodium|potassium|platelet|hemoglobin|haemoglobin|lipid|glucose|sugar|widal|malaria|dengue)\b/.test(value)) return 'pathology';
  return 'non-pathology';
};

const requisitionKindForCharge = (line: InvestigationBill['charges'][number]): RequisitionKind =>
  requisitionKindForLabel(`${line.category?.name ?? ''} ${line.category?.legacyId ?? ''}`)
  ?? requisitionKindForLabel(`${line.subCategory?.name ?? ''} ${line.subCategory?.legacyId ?? ''}`)
  ?? requisitionKindForSection(line.requisitionSection)
  ?? requisitionKindForChargeName(line.chargeName);

export const InvestigationBillDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState<BillTab>('bill-details');
  const query = useInvestigationBill(id);
  useEffect(() => {
    if (query.data?.id && params.get('print') === '1') void navigate(`/investigation-bill/${query.data.id}/print-bill`, { replace: true });
  }, [navigate, params, query.data?.id]);
  if (query.isLoading) return <DetailSkeleton label="Loading bill details" />;
  if (query.isError || !query.data) return <ErrorState title="Investigation bill not found" message="The bill could not be loaded." />;
  const row = query.data;
  const address = [row.patientSnapshot.address, row.patientSnapshot.district, row.patientSnapshot.state, row.patientSnapshot.pinCode].filter(Boolean).join(', ') || '-';
  const billStatusLabel = row.billing.billStatus === 'due' ? 'Due' : row.billing.billStatus === 'cancelled' ? 'Cancelled' : 'Paid';
  const activeBlankTab = blankTabs.find((tab) => tab.key === activeTab);
  const requisitionBillCharges = row.charges.map((line) => ({ id: line.charge, label: line.chargeName, rate: line.rate, kind: requisitionKindForCharge(line) }));
  const printBill = () => openAuthenticatedRoute(`/investigation-bill/${row.id}/print-bill`);
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) void navigate(-1);
    else void navigate('/investigation-bill');
  };

  return (
    <div className="page-stack investigation-bill-page investigation-bill-detail-page">
      <div className="page-heading investigation-bill-print-hidden">
        <div><p className="eyebrow">Investigation</p><h1>Investigation Bill Details</h1></div>
        <div className="investigation-bill-heading-actions">
          <button className="button button-primary" type="button" onClick={printBill}>Print</button>
          <button className="button button-secondary" type="button" onClick={goBack}>Back</button>
        </div>
      </div>

      <div className="investigation-bill-detail-layout">
        <aside className="content-card investigation-bill-detail-sidebar">
          <div className="investigation-bill-detail-patient-box">
            <p><strong>Patient Name : </strong>{row.patientSnapshot.name}</p>
            <p><strong>UHID : </strong>{row.patientSnapshot.uhid ?? '-'}</p>
            <p><strong>Sex : </strong>{row.patientSnapshot.gender} || <strong>Age : </strong>{row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</p>
            <p><strong>Mobile : </strong>{row.patientSnapshot.phone ?? '-'}</p>
            <p><strong>Address : </strong>{address}</p>
            <p><strong>Referring Doctor : </strong>{doctorNameOrDash(row.referringDoctor?.name)}</p>
          </div>
          <BillActionGrid classPrefix="investigation-bill-detail" printHiddenClass="investigation-bill-print-hidden">
            <BillActionTile classPrefix="investigation-bill-detail" icon={IconIdCard} label="Patient Profile" to={`/patients/${row.patient.id}`} />
            <BillActionTile classPrefix="investigation-bill-detail" icon={IconBillCheck} label="Bill Details" active={activeTab === 'bill-details'} onClick={() => setActiveTab('bill-details')} />
            <BillActionTile classPrefix="investigation-bill-detail" icon={IconStore} label="Requisition" active={activeTab === 'requisition'} onClick={() => setActiveTab('requisition')} />
            {blankTabs.map((tab) => (
              tab.key === 'prescription' ? (
                <BillActionHoverTile
                  key={tab.key}
                  classPrefix="investigation-bill-detail"
                  icon={tab.icon}
                  label={tab.label}
                  items={[
                    { label: 'Prescription without letterhead', onClick: () => openAuthenticatedRoute(`/investigation-bill/${row.id}/print-prescription?header=0`) },
                    { label: 'Prescription with letterhead', onClick: () => openAuthenticatedRoute(`/investigation-bill/${row.id}/print-prescription?header=1`) },
                  ]}
                />
              ) : tab.key === 'print-bill' ? (
                <BillActionTile key={tab.key} classPrefix="investigation-bill-detail" icon={tab.icon} label={tab.label} onClick={printBill} />
              ) : (
                <BillActionTile key={tab.key} classPrefix="investigation-bill-detail" icon={tab.icon} label={tab.label} active={activeTab === tab.key} soon={tab.soon} onClick={() => setActiveTab(tab.key)} />
              )
            ))}
          </BillActionGrid>
        </aside>

        <section className="content-card investigation-bill-detail-panel">
          {activeTab === 'edit-bill' ? (
            <InvestigationBillEditBillForm row={row} onSaved={() => setActiveTab('bill-details')} />
          ) : activeTab === 'all-bill' ? (
            <InvestigationBillAllBillList row={row} />
          ) : activeTab === 'all-receipt' ? (
            <InvestigationBillAllReceiptList row={row} />
          ) : activeTab === 'all-refund' ? (
            <InvestigationBillAllRefundList row={row} />
          ) : activeTab === 'requisition' ? (
            <IpdRequisitionPanel
              context={{
                patientName: row.patientSnapshot.name,
                uhid: row.patientSnapshot.uhid,
                primaryLabel: 'Investigation ID',
                primaryValue: row.investigationNumber,
                secondaryText: `Bill No ${row.billing.billNumber}`,
              }}
              billCharges={requisitionBillCharges}
            />
          ) : activeBlankTab ? (
            <EmptyState
              title={`${activeBlankTab.label}${activeBlankTab.soon ? ' — Coming soon' : ''}`}
              message={activeBlankTab.message}
              action={{ label: 'Back to Bill Details', onClick: () => setActiveTab('bill-details') }}
            />
          ) : (
          <>
          <header className="investigation-bill-detail-banner">
            <div className="investigation-bill-detail-banner-row">
              <div className="investigation-bill-detail-banner-fields">
                <span>Bill No : {row.billing.billNumber}</span>
                <span>Bill Date : {formatInvestigationDateTime(row.billDate)}</span>
                <span>Bill Section : Investigation</span>
              </div>
              <span className={`investigation-bill-badge ${row.billing.billStatus}`}>{billStatusLabel}</span>
            </div>
            <p>Bill Created By - {row.generatedBy.name}</p>
          </header>

          <div className="table-scroll">
            <table className="investigation-bill-detail-charge-table">
              <thead><tr><th>#</th><th>Date</th><th>Charge Name</th><th>Charge Amount</th><th>Qty</th><th>Dis Amt</th><th>Dis %</th><th>Status</th><th>Total</th></tr></thead>
              <tbody>
                {row.charges.length === 0
                  ? <tr><td className="investigation-bill-detail-empty" colSpan={9}>No itemized charge rows were stored for this legacy bill.</td></tr>
                  : row.charges.map((line, index) => (
                    <tr key={line.charge} className={row.billing.billStatus === 'paid' ? 'paid' : undefined}>
                      <td>{index + 1}</td>
                      <td>{formatInvestigationDateTime(row.billDate)}</td>
                      <td>{line.chargeName}</td>
                      <td>{currency(line.rate)}</td>
                      <td>{line.quantity}</td>
                      <td>{currency(line.discountAmount)}</td>
                      <td>{line.discountPercent}%</td>
                      <td><span className={`investigation-bill-badge ${row.billing.billStatus}`}>{billStatusLabel}</span></td>
                      <td>{currency(line.amount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="investigation-bill-detail-summary">
            <div><span>Total</span><strong>{currency(row.billing.total)}</strong></div>
            <div><span>Discount</span><strong>{currency(row.billing.totalDiscount)}</strong></div>
            <div><span>Payable Amount</span><strong>{currency(row.billing.grandTotal)}</strong></div>
            <div><span>Total Payment</span><strong>{currency(row.billing.totalPayment)}</strong></div>
            <div className={row.billing.dueAmount > 0 ? 'due' : undefined}><span>Due</span><strong>{currency(row.billing.dueAmount)}</strong></div>
          </div>

          <section className="investigation-bill-detail-subsection">
            <div className="investigation-bill-detail-subsection-head">
              <h3>Payment / Receipt List</h3>
              <span>Total Payment <strong>{currency(row.billing.totalPayment)}</strong></span>
            </div>
            {row.payments.length === 0 ? (
              <p className="investigation-bill-detail-empty-note">No payments recorded for this bill.</p>
            ) : (
              <div className="table-scroll">
                <table className="investigation-bill-detail-payment-table">
                  <thead><tr><th>#</th><th>Mode</th><th>Bank</th><th>Amount</th></tr></thead>
                  <tbody>
                    {row.payments.map((payment, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{payment.mode}</td>
                        <td>{payment.bankName ?? '-'}</td>
                        <td>{currency(payment.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="investigation-bill-detail-subsection">
            <h3>Referral Details</h3>
            <dl className="investigation-bill-detail-referral">
              <div><dt>Market By</dt><dd>{row.marketBy?.name ?? '-'}</dd></div>
              <div><dt>Provider</dt><dd>{row.provider?.name ?? '-'}</dd></div>
              <div><dt>Referred By</dt><dd>{row.referredBy?.name ?? '-'}</dd></div>
            </dl>
          </section>
          </>
          )}
        </section>
      </div>
    </div>
  );
};
